import ModalButton from "@/Components/ModalButton"
import Modal from "@/Components/Modal"

interface FastPaceWarningModalProps {
  onConfirm: () => void
  onCancel: () => void
  /** Dims what is behind the panel. On in-game modals nothing does, so this is
   *  off by default and the lobby turns it on — there the panel would otherwise
   *  float over a settings grid that stays clickable underneath it. */
  backdrop?: boolean
}

/**
 * "Are you sure?" in front of fast pace, for a player who has barely played.
 *
 * Shared by the two places fast pace can be turned on by one person with nobody
 * to check them: the lobby toggle (NewGameSettings) and the in-game button in a
 * game full of bots (FastPaceNewPlayerConfirmModal). One component so the two
 * cannot end up explaining the same setting differently.
 *
 * It says the two things that are not obvious from the words "fast pace": that
 * the game gets harder to follow, and that auction results stop appearing on
 * their own — the second being the part that reads as something having gone
 * wrong, so it is named next to the button that brings them back.
 *
 * Purely presentational: whoever opens it owns what confirming means.
 */
const FastPaceWarningModal: React.FC<FastPaceWarningModalProps> = ({
  onConfirm,
  onCancel,
  backdrop,
}) => (
  <>
    {backdrop && (
      <div
        className="fixed inset-0 z-[299] bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
    )}
    <Modal
      data-testid="game.fast-pace-new-player-confirm-modal"
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[300] w-[90%] md:w-auto max-w-md"
      icon={<i className="fa-solid fa-bolt text-white text-2xl" />}
      title="Go fast pace?"
      subtitle="You're still new to the game"
      contentClassName="flex flex-col gap-6 px-6 md:px-8 pb-8"
      onClose={onCancel}
      closeTestId="game.fast-pace-new-player-confirm-close-btn"
    >
      <div className="flex flex-col gap-3 font-poppins text-gray-200">
        <div className="text-center text-base font-medium">
          Everything moves faster, and animations are cut short — hard to follow
          when you're new.
        </div>
        {/* The one surprise worth naming, next to the button that undoes it. */}
        <div className="text-center text-sm text-white/60">
          Auction results won't show up on their own. Open the last one from{" "}
          <span className="font-semibold text-emerald-200">
            Last Auction Results
          </span>{" "}
          in the bottom corner.
        </div>
      </div>

      <div className="flex justify-center gap-4">
        <ModalButton
          size="md"
          color="default"
          onClick={onCancel}
          data-testid="game.fast-pace-new-player-confirm-cancel-btn"
        >
          <div className="text-white font-medium font-poppins text-lg">
            Cancel
          </div>
        </ModalButton>
        <ModalButton
          size="md"
          color="yellow"
          onClick={onConfirm}
          data-testid="game.fast-pace-new-player-confirm-accept-btn"
        >
          <div className="flex items-center gap-2">
            <i className="text-white fa-solid fa-bolt fa-lg"></i>
            <div className="text-white font-medium font-poppins text-lg">
              Go fast pace
            </div>
          </div>
        </ModalButton>
      </div>
    </Modal>
  </>
)

export default FastPaceWarningModal
